import {i18n} from '@mira/i18n';
import {match, Pattern} from 'ts-pattern';

import {isEmpty, isNil} from 'ramda';

const HTTP_UNPROCESSABLE_ENTITY = 422;
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_CLIENT_ERROR_MIN = 400;
const HTTP_CLIENT_ERROR_MAX = 499;
const HTTP_SERVER_ERROR_MIN = 500;
const HTTP_SERVER_ERROR_MAX = 599;

/**
 * User-facing error copy derived from a prediction error returned by RTK
 * Query. Two layers — `title` for the heading, `description` for the body —
 * so consumers can lay them out consistently.
 */
export interface PredictionErrorMessage {
  title: string;
  description: string;
}

interface RtkQueryFetchError {
  status: number | string;
  data?: unknown;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && !isNil(value);
}

function isFetchBaseQueryError(error: unknown): error is RtkQueryFetchError {
  if (!isObject(error) || !('status' in error)) {
    return false;
  }
  const status = error.status;
  return typeof status === 'number' || typeof status === 'string';
}

/**
 * Surfaces the failing field names from a 422 `detail` payload as a
 * one-liner — e.g. `make, vehicle_data.car_style`. Empty string when the
 * shape is unexpected (callers fall back to generic copy).
 */
function describeValidationError(data: unknown): string {
  if (!isObject(data)) {
    return '';
  }
  const detail = data.detail;
  if (!Array.isArray(detail) || detail.length === 0) {
    return '';
  }
  const fieldNames = detail
    .map((entry: unknown): string | null => {
      if (!isObject(entry)) {
        return null;
      }
      const loc = entry.loc;
      if (!Array.isArray(loc)) {
        return null;
      }
      return loc.filter((p) => p !== 'body').join('.');
    })
    .filter((s): s is string => s !== null && s.length > 0);
  return Array.from(new Set(fieldNames)).join(', ');
}

const getGeneric = (): PredictionErrorMessage => ({
  title: i18n.t('predictionError.generic.title'),
  description: i18n.t('predictionError.generic.description'),
});

const getServiceUnavailable = (): PredictionErrorMessage => ({
  title: i18n.t('predictionError.unavailable.title'),
  description: i18n.t('predictionError.unavailable.description'),
});

const getAccessDenied = (): PredictionErrorMessage => ({
  title: i18n.t('predictionError.access.title'),
  description: i18n.t('predictionError.access.description'),
});

const getUnsupportedVehicle = (): PredictionErrorMessage => ({
  title: i18n.t('predictionError.unsupported.title'),
  description: i18n.t('predictionError.unsupported.description'),
});

/**
 * Build the 422 message variant. Split out so the main `match` can branch on
 * "has invalid fields" vs "no field info" without a ternary inside the handler.
 */
function getUnsupportedVehicleMessage(data: unknown): PredictionErrorMessage {
  const fields = describeValidationError(data);
  return match(fields)
    .returnType<PredictionErrorMessage>()
    .when(isEmpty, () => getUnsupportedVehicle())
    .otherwise((validFields) => ({
      ...getUnsupportedVehicle(),
      description: i18n.t('predictionError.unsupported.descriptionWithFields', {
        fields: validFields,
      }),
    }));
}

/**
 * Map an RTK Query / fetch error into user-facing prediction error copy.
 *
 * Heuristics:
 * - HTTP 422 → `Vehicle not supported` with the failing field path appended.
 * - HTTP 401/403 → auth/access copy.
 * - Other HTTP 4xx → `Vehicle not supported` (data the backend rejects).
 * - HTTP 5xx / network failure → `Service temporarily unavailable`.
 * - Anything else → generic.
 */
export function getPredictionErrorMessage(error: unknown): PredictionErrorMessage {
  if (!isFetchBaseQueryError(error)) {
    return getGeneric();
  }

  return match(error.status)
    .returnType<PredictionErrorMessage>()
    .with(HTTP_UNPROCESSABLE_ENTITY, () => getUnsupportedVehicleMessage(error.data))
    .with(HTTP_UNAUTHORIZED, HTTP_FORBIDDEN, () => getAccessDenied())
    .with(Pattern.number.between(HTTP_CLIENT_ERROR_MIN, HTTP_CLIENT_ERROR_MAX), () =>
      getUnsupportedVehicle()
    )
    .with(Pattern.number.between(HTTP_SERVER_ERROR_MIN, HTTP_SERVER_ERROR_MAX), () =>
      getServiceUnavailable()
    )
    .with('FETCH_ERROR', 'TIMEOUT_ERROR', () => getServiceUnavailable())
    .otherwise(() => getGeneric());
}
